import {
  useState,
  useContext,
  useRef,
  useEffect,
  Suspense,
  createContext,
  SyntheticEvent,
  ReactNode,
  Dispatch,
  SetStateAction,
  RefObject,
} from "react";
import styled, { css, keyframes } from "styled-components/macro";
import { MemoryRouter } from "react-router";
import Portal from "./Portal";
import { Box, BoxHeader, LoadingBoxContent } from "./Box";
import useStackContext, {
  StackContextProvider,
} from "../hooks/use-stack-context";
import useFocusedElement from "../hooks/use-focused-element";

export type SidePanelTrigger = (props: {
  show: (e?: SyntheticEvent) => void;
  ref: RefObject<any>;
}) => ReactNode;

export type SidePanelContent = (props: {
  hide: (e?: SyntheticEvent) => void;
}) => ReactNode;

type Context = {
  sidebarCount: number;
  setSidebarCount: Dispatch<SetStateAction<number>>;
};

const context = createContext<null | Context>(null);

type ClickOutsideContext = {
  preventClickOutsideSet: Set<{}>;
  setPreventClickOutsideSet: Dispatch<SetStateAction<Set<{}>>>;
};

export const preventClickOutsideContext = createContext<null | ClickOutsideContext>(
  null
);

export const Provider = ({ children }: { children: ReactNode }) => {
  const [sidebarCount, setSidebarCount] = useState(0);

  return (
    <context.Provider value={{ sidebarCount, setSidebarCount }}>
      {children}
    </context.Provider>
  );
};

function SidePanel({
  trigger,
  content,
  isShowing: propsIsShowing,
  onHide,
  onRemove,
}: {
  trigger: SidePanelTrigger;
  content: SidePanelContent;
  isShowing?: boolean;
  onHide?: () => void;
  onRemove?: () => void;
}) {
  const [randomKey, setRandomKey] = useState(0);
  const { sidebarCount, setSidebarCount } = useContext(context)!;
  const [sidebarIndex, setSidebarIndex] = useState(0);
  const [isShowing, setIsShowing] = useState(false);
  const [removeEntirely, setRemoveEntirely] = useState(true);
  const triggerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const removeEntirelyTimeout = useRef<number>(0);
  const zIndex = useStackContext();
  const getFocusedElement = useFocusedElement();

  const [preventClickOutsideSet, setPreventClickOutsideSet] = useState<Set<{}>>(
    new Set()
  );

  const [jiggle, setJiggle] = useState(false);

  const preventClickOutside = preventClickOutsideSet.size > 0;

  const show = (e?: SyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setSidebarCount((sidebarCount) => {
      const newCount = sidebarCount + 1;
      setRandomKey(Math.random() + Date.now());

      setSidebarIndex(newCount);
      setIsShowing(true);

      window.clearTimeout(removeEntirelyTimeout.current);
      setRemoveEntirely(false);
      window.setTimeout(() => {
        if (!contentRef.current) return;

        // only focus on the content box if the trigger still
        // has focus
        if (triggerRef.current === getFocusedElement()) {
          contentRef.current.focus();
        }
      }, 1000);

      return newCount;
    });
  };

  const hide = (e?: SyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (onHide) onHide();

    setSidebarCount((sidebarCount) => {
      setIsShowing(false);

      if (contentRef.current) contentRef.current.blur();
      else document.body.focus();

      if (
        triggerRef.current &&
        triggerRef.current.focus &&
        !triggerRef.current?.hasAttribute("data-reach-tooltip-trigger")
      )
        triggerRef.current.focus();

      removeEntirelyTimeout.current = window.setTimeout(() => {
        setRemoveEntirely(true);
        setRandomKey(Math.random() + Date.now());
        if (onRemove) onRemove();
      }, 1000);
      return sidebarCount - 1;
    });
  };

  useEffect(() => {
    if (propsIsShowing === undefined) return;
    if (propsIsShowing) {
      show();
    } else {
      if (isShowing) {
        hide();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsIsShowing]);

  const opacity = Math.max(0, 1 - (sidebarCount - sidebarIndex - 2) / 2);
  const hidden = opacity === 0;

  return (
    <>
      {trigger({ show, ref: triggerRef })}
      {!removeEntirely && (
        <Portal>
          <SlideLeftContainer
            key={randomKey}
            hidden={hidden}
            jiggle={jiggle}
            onDoubleClick={hide}
            onClick={(e) => {
              if (!preventClickOutside) hide(e);
              else {
                setJiggle(true);
                setTimeout(() => {
                  setJiggle(false);
                }, 500);
              }
            }}
            style={{
              pointerEvents: isShowing ? undefined : "none",
              zIndex,
              opacity,
              transform: `translate3d(-${
                (sidebarCount - sidebarIndex) * 5 + "px"
              },0,0) scale(${1 - (sidebarCount - sidebarIndex) * 0.05})`,
            }}
          >
            <Container
              show={isShowing}
              aria-hidden={sidebarIndex !== sidebarCount}
              inert={sidebarIndex !== sidebarCount}
              onDoubleClick={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              tabIndex={-1}
              ref={contentRef}
              onKeyDown={(e) => {
                if (e.key !== "Escape") return;
                if (!preventClickOutside) hide(e);
                else {
                  setJiggle(true);
                  setTimeout(() => {
                    setJiggle(false);
                  }, 500);
                }
              }}
            >
              <preventClickOutsideContext.Provider
                value={{ preventClickOutsideSet, setPreventClickOutsideSet }}
              >
                <StackContextProvider>
                  <Suspense
                    fallback={
                      <>
                        <BoxHeader />
                        <LoadingBoxContent />
                      </>
                    }
                  >
                    <MemoryRouter>{content({ hide })}</MemoryRouter>
                  </Suspense>
                </StackContextProvider>
              </preventClickOutsideContext.Provider>
            </Container>
          </SlideLeftContainer>
        </Portal>
      )}
    </>
  );
}

const keyframesInRight = keyframes`
  from {
    transform: translateX(150%);
  }
  to {
    transform: translateX(0%);
  }
`;

const keyframesOutRight = keyframes`
  from {
    transform: translateX(0%);
  }
  to {
    transform: translateX(150%);
  }
`;

const keyframesInDown = keyframes`
  from {
    transform: translateY(150%);
  }
  to {
    transform: translateY(0%);
  }
`;

const keyframesOutDown = keyframes`
  from {
    transform: translateY(0%);
  }
  to {
    transform: translateY(150%);
  }
`;

const jiggle = keyframes`
  0% {
    transform: translateX(0%);
  }
  50% {
    transform: translateX(15px);
  }
  75% {
    transform: translateX(-10px);
  }
  100% {
    transform: translateX(0px);
  }
`;

const Container = styled(Box)<{ show: boolean; inert: boolean }>`
  position: absolute;
  z-index: 100;
  box-shadow: 0 30px 90px -20px rgba(0, 0, 0, 0.3);
  @media (min-width: 800px) {
    top: 16px;
    bottom: 16px;
    right: 16px;
    width: 625px;
    animation: 0.5s ${keyframesOutRight};
    animation-fill-mode: forwards;
    ${(p) =>
      p.show &&
      css`
        animation: 0.3s ${keyframesInRight};
        animation-fill-mode: forwards;
        animation-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
      `}
  }
  @media (max-width: 790px) {
    animation: 0.5s ${keyframesOutDown};
    animation-fill-mode: forwards;
    top: 30px;
    left: 0;
    bottom: 0;
    right: 0;
    ${(p) =>
      p.show &&
      css`
        animation: 0.3s ${keyframesInDown};
        animation-fill-mode: forwards;
        animation-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
      `}
  }
`;

const SlideLeftContainer = styled.div<{ jiggle: boolean }>`
  transition-duration: 0.15s;
  transition-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  ${(p) =>
    p.jiggle &&
    css`
      animation: 0.5s ${jiggle};
      animation-fill-mode: forwards;
      animation-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
    `}
`;

export default SidePanel;
